import { useState, useEffect, useRef } from "react";
import useSWR from "swr";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, Sparkle, Trash, CheckCircle, Eye, EyeSlash, ImageSquare, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ProviderCredentialsCard from "@/components/ProviderCredentialsCard";
import SmtpSettingsCard from "@/components/SmtpSettingsCard";
import StorageSettingsCard from "@/components/StorageSettingsCard";
import SecuritySettingsCard from "@/components/SecuritySettingsCard";
import AmazonCookiesCard from "@/components/AmazonCookiesCard";
import Pagination from "@/components/Pagination";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Settings() {
    const { user, refresh: refreshUser } = useAuth();
    const { data: creds, mutate } = useSWR("/amazon/credentials", fetcher);
    const { data: history } = useSWR("/sync/history", fetcher, { refreshInterval: 5000 });
    const { data: snapshots } = useSWR("/sync/debug-snapshots", fetcher, { refreshInterval: 5000 });
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);

    // Account update form — uncontrolled inputs via refs to keep React lint
    // happy (no "setState-in-effect" sync warnings) and still let us drive a
    // single save handler.
    const acctEmailRef = useRef(null);
    const acctNameRef = useRef(null);
    const acctNewPwdRef = useRef(null);
    const acctCurPwdRef = useRef(null);
    const [acctShow, setAcctShow] = useState(false);
    const [acctSaving, setAcctSaving] = useState(false);

    // Pagination for the sync-history list (10 rows per page)
    const [histPage, setHistPage] = useState(1);
    const HIST_PER_PAGE = 10;
    const [acctNeedsConfirm, setAcctNeedsConfirm] = useState(false);

    const onAcctChange = () => {
        // Toggle confirmation field when user changes email or fills new password
        const emailChanged = acctEmailRef.current?.value !== (user?.email || "");
        const newPwd = (acctNewPwdRef.current?.value || "").length > 0;
        setAcctNeedsConfirm(emailChanged || newPwd);
    };

    useEffect(() => {
        if (creds?.email) setEmail(creds.email);
    }, [creds?.email]);

    // No useEffect needed — inputs use defaultValue from user (uncontrolled).


    const saveAccount = async () => {
        const acctEmail = acctEmailRef.current?.value || "";
        const acctName = acctNameRef.current?.value || "";
        const acctNewPwd = acctNewPwdRef.current?.value || "";
        const acctCurPwd = acctCurPwdRef.current?.value || "";
        const sensitive = acctEmail !== user?.email || acctNewPwd;
        if (sensitive && !acctCurPwd) {
            toast.error("Saisissez votre mot de passe actuel pour confirmer.");
            return;
        }
        setAcctSaving(true);
        try {
            const payload = { name: acctName };
            if (acctEmail !== user?.email) payload.email = acctEmail;
            if (acctNewPwd) payload.new_password = acctNewPwd;
            if (sensitive) payload.current_password = acctCurPwd;
            await api.put("/auth/me", payload);
            toast.success("Compte mis à jour");
            if (acctNewPwdRef.current) acctNewPwdRef.current.value = "";
            if (acctCurPwdRef.current) acctCurPwdRef.current.value = "";
            setAcctNeedsConfirm(false);
            if (refreshUser) await refreshUser();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
        } finally {
            setAcctSaving(false);
        }
    };

    const save = async () => {
        if (!email || !password) {
            toast.error("Saisissez email et mot de passe Amazon");
            return;
        }
        setSaving(true);
        try {
            await api.put("/amazon/credentials", { email, password, marketplace: "amazon.fr" });
            toast.success("Identifiants Amazon enregistrés (chiffrés)");
            setPassword("");
            mutate();
        } catch (e) {
            toast.error("Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Supprimer vos identifiants Amazon enregistrés ?")) return;
        await api.delete("/amazon/credentials");
        setEmail("");
        setPassword("");
        toast.success("Identifiants supprimés");
        mutate();
    };

    const seedDemo = async () => {
        try {
            await api.post("/demo/seed");
            toast.success("24 factures de démo chargées");
        } catch (e) {
            toast.error("Erreur");
        }
    };

    const clearDemo = async () => {
        await api.delete("/demo/clear");
        toast.success("Données de démo supprimées");
    };

    return (
        <div className="px-6 lg:px-10 py-8 max-w-[1100px] mx-auto">
            <div className="mb-8">
                <p className="overline">Paramètres</p>
                <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
                    Configuration
                </h1>
                <p className="text-muted-foreground text-sm mt-2 max-w-lg">
                    Connectez votre compte Amazon FR, gérez les données de démo et consultez l’historique des synchros.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* User account */}
                <Card className="ec-card lg:col-span-2" data-testid="account-card">
                    <CardContent className="p-6 space-y-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                                    <User size={18} weight="duotone" />
                                    Mon compte ScanBox
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Modifie ton email, ton nom affiché et ton mot de passe.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label htmlFor="acct-email">Email de connexion</Label>
                                <Input
                                    id="acct-email"
                                    type="email"
                                    ref={acctEmailRef}
                                    defaultValue={user?.email || ""}
                                    onChange={onAcctChange}
                                    key={`em-${user?.email || ""}`}
                                    data-testid="acct-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="acct-name">Nom affiché</Label>
                                <Input
                                    id="acct-name"
                                    ref={acctNameRef}
                                    defaultValue={user?.name || ""}
                                    key={`nm-${user?.name || ""}`}
                                    placeholder="Ton prénom ou pseudo"
                                    data-testid="acct-name"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="acct-new-pwd">Nouveau mot de passe <span className="text-xs text-muted-foreground">(laisse vide pour ne pas changer)</span></Label>
                            <div className="relative">
                                <Input
                                    id="acct-new-pwd"
                                    type={acctShow ? "text" : "password"}
                                    ref={acctNewPwdRef}
                                    onChange={onAcctChange}
                                    placeholder="••••••• (minimum 6 caractères)"
                                    autoComplete="new-password"
                                    data-testid="acct-new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setAcctShow((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                >
                                    {acctShow ? <EyeSlash size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {acctNeedsConfirm && (
                            <div className="space-y-2 pt-2 border-t border-border">
                                <Label htmlFor="acct-cur-pwd" className="text-amber-700">
                                    Mot de passe actuel <span className="text-xs">(confirmation requise)</span>
                                </Label>
                                <Input
                                    id="acct-cur-pwd"
                                    type="password"
                                    ref={acctCurPwdRef}
                                    placeholder="Ton mot de passe actuel"
                                    autoComplete="current-password"
                                    data-testid="acct-current-password"
                                />
                            </div>
                        )}

                        <div className="flex justify-end">
                            <Button
                                onClick={saveAccount}
                                disabled={acctSaving}
                                className="bg-primary hover:bg-primary/90"
                                data-testid="acct-save"
                            >
                                {acctSaving ? "Enregistrement…" : "Enregistrer"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* 2FA security — placed right under the account/password card */}
                <div className="lg:col-span-3">
                    <SecuritySettingsCard />
                </div>

                {/* Provider credentials (Amazon, OVH) */}
                <div className="lg:col-span-2">
                    <ProviderCredentialsCard />
                </div>

                {/* Amazon cookies mode (low-RAM HTTPX scraper via Chrome extension) */}
                <div className="lg:col-span-3">
                    <AmazonCookiesCard />
                </div>

                {/* SMTP system config */}
                <div className="lg:col-span-3">
                    <SmtpSettingsCard />
                    <StorageSettingsCard />
                </div>

                {/* Demo */}
                <Card className="ec-card">
                    <CardContent className="p-6 space-y-4">
                        <h3 className="font-display text-lg font-bold tracking-tight">Données de démo</h3>
                        <p className="text-xs text-muted-foreground">
                            Chargez 24 factures fictives pour explorer l’application sans Amazon.
                        </p>
                        <div className="space-y-2">
                            <Button variant="outline" className="w-full" onClick={seedDemo} data-testid="seed-demo-settings">
                                <Sparkle size={16} className="mr-2" />
                                Charger la démo
                            </Button>
                            <Button variant="ghost" className="w-full text-destructive" onClick={clearDemo} data-testid="clear-demo">
                                Effacer la démo
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Sync history */}
                <Card className="ec-card lg:col-span-3">
                    <CardContent className="p-6">
                        <div className="flex items-baseline justify-between mb-4">
                            <h3 className="font-display text-lg font-bold tracking-tight">Historique des synchronisations</h3>
                            {history?.length > 0 && (
                                <span className="text-xs text-muted-foreground" data-testid="history-count">
                                    {history.length} entrée{history.length > 1 ? "s" : ""}
                                </span>
                            )}
                        </div>
                        {(!history || history.length === 0) ? (
                            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
                                Aucune synchronisation effectuée pour le moment.
                            </div>
                        ) : (
                            <>
                                <div className="divide-y divide-border">
                                    {history
                                        .slice((histPage - 1) * HIST_PER_PAGE, histPage * HIST_PER_PAGE)
                                        .map((h) => (
                                        <div key={h.id} className="flex items-center justify-between py-3" data-testid={`history-${h.id}`}>
                                            <div className="min-w-0 flex items-center gap-3">
                                                <Badge
                                                    variant="outline"
                                                    className={
                                                        h.status === "success"
                                                            ? "text-emerald-700 border-emerald-200"
                                                            : h.status === "failed"
                                                                ? "text-destructive border-destructive/30"
                                                                : "text-muted-foreground"
                                                    }
                                                >
                                                    {h.status}
                                                </Badge>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold truncate">{h.message || "—"}</div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {h.started_at ? fmtDate(h.started_at) : "—"} · {h.invoices_found} factures
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {history.length > HIST_PER_PAGE && (
                                    <Pagination
                                        page={histPage}
                                        total={history.length}
                                        perPage={HIST_PER_PAGE}
                                        onChange={setHistPage}
                                        testIdPrefix="history-pagination"
                                    />
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Debug snapshots */}
                {snapshots && snapshots.length > 0 && (
                    <Card className="ec-card lg:col-span-3 border-amber-200" data-testid="debug-snapshots">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-2 mb-1">
                                <ImageSquare weight="duotone" size={20} className="text-amber-600" />
                                <h3 className="font-display text-lg font-bold tracking-tight">Captures de debug</h3>
                            </div>
                            <p className="text-xs text-muted-foreground mb-4">
                                Quand Amazon bloque la connexion (captcha, vérification, etc.), une capture d’écran est enregistrée
                                ici pour comprendre ce qui s’est passé. Cliquez pour ouvrir.
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {snapshots.slice(0, 12).map((s) => (
                                    <a
                                        key={s.name}
                                        href={`${process.env.REACT_APP_BACKEND_URL}${s.url}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block border border-border rounded-md overflow-hidden hover:border-primary transition-colors"
                                        data-testid={`snapshot-${s.name}`}
                                    >
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`}
                                            alt={s.name}
                                            className="w-full h-32 object-cover object-top bg-accent"
                                        />
                                        <div className="p-2">
                                            <div className="text-xs font-mono truncate">{s.name}</div>
                                            <div className="text-xs text-muted-foreground">{fmtDate(s.modified)}</div>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
